/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { isEmpty, isUndefined } from 'lodash';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiService, ExtendedCompanyInfo, MeasurementDate } from 'api';
import { companiesAction, firmsAction, fundsAction, globalAction } from 'common/actions';
import { ERROR_403, ERROR_404, ERROR_504 } from 'common/config/api';
import { forbiddenPath } from 'common/config/urls';
import { DOCUMENTS_SLUG } from 'common/constants/documents';
import { FIRM_REQUESTS_SLUG } from 'common/constants/firms';
import { PERIOD_CHAR, PERIOD_CODE } from 'common/constants/general';
import { SCALAR_OTP_COOKIE, SIGN_IN_URL } from 'common/constants/login';
import { EXCEL_EXPORT } from 'common/constants/pageActions';
import { PDF_EXPORT_QUERY_PARAM_EXPORT_MODE, PDF_EXPORT_QUERY_PARAM_EXPORT_TAB } from 'common/constants/pdfExport';
import {
  AUDITOR_VALUE,
  EXPORT_VIEWER_VALUE,
  FULL_ACCESS_VIEWER_VALUE,
  VIEW_ONLY_NO_ACTIONS_ROLES_VALUES,
} from 'common/constants/user';
import { useStore } from 'common/store';
import { CompanyInfo, FirmCompany } from 'common/types/company';
import { Fund } from 'common/types/fund';
import { Alert, Filter, Header } from 'components';
import { ActionProps, MenuOptionsProps } from 'components/ActionButtons/types';
import ExcelExportContext, { ExcelExportContextType } from 'context/ExcelExportContext';
import LayoutContext, { LayoutContextType } from 'context/LayoutContext';
import { Sidebar } from 'layouts/Main/components/Sidebar/components';
import { FundInvestmentFormDialog } from 'pages/FundInvestments/components';
import { FundDialogForm } from 'pages/Funds/forms';
import { FirmService } from 'services';
import { useCheckReportFile, useDocuments } from 'services/hooks';
import { useHandleSingleCompanyUser } from 'services/hooks/company';
import { useGetCompanyListByFirmId, useGetFundListByFirmId } from 'services/hooks/firm';
import { useHandleSingleFundUser } from 'services/hooks/fund';
import { useGetLowerUserPermissions } from 'services/hooks/users';
import { useDialogFormsStore } from 'store';
import { checkCookie, getCurrency, getExchangeRate, getNumberValue, isFalseStrict } from 'utilities';
import { FirmStepper, Footer, NewCompanyDialog } from './components';
import { FilterOption, FirmResponseParams, MainParams, MainProps, UseStoreValuesExtended } from './Main.props';
import { HEADER_HEIGHT } from '../../theme';

const Root = styled('div')<{ shouldDisplayGrayBg?: boolean }>(({ theme, shouldDisplayGrayBg }) => ({
  backgroundColor: shouldDisplayGrayBg ? 'transparent' : theme.palette.white,
  height: '100%',
  paddingLeft: '60px',
  [theme.breakpoints.up('sm')]: {},
}));

const Content = styled('main')<{ shouldDisplayGrayBg?: boolean }>(({ theme, shouldDisplayGrayBg }) => ({
  padding: theme.spacing(2),
  marginTop: `${HEADER_HEIGHT}px`,
  // Set the footer at the bottom of the screen when the content is not enough
  minHeight: 'calc(100vh - 250px)',
  backgroundColor: shouldDisplayGrayBg ? 'transparent' : theme.palette.white,
}));

const StyledGridFilter = styled(Grid)(({ theme }) => ({
  '&&': {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
}));

const keepPollingStatusCodesForSingleExcelExport = [Number(ERROR_404), Number(ERROR_504)];

const Main = ({ children, ...props }: MainProps) => {
  const location = useLocation();
  const { firmSlugParam, companySlugParam, tableSlugParam } = useParams<MainParams>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { getCompanies } = useGetCompanyListByFirmId();
  const { getFunds } = useGetFundListByFirmId();
  const [, getLowerUserPermissions] = useGetLowerUserPermissions();
  const { downloadDocument } = useDocuments();

  const onSuccessForIndividualReport = useCallback(
    (onSuccessData: any, requestData: any) =>
      downloadDocument({
        file: onSuccessData,
        firmId: requestData.firm,
      }),
    []
  );

  const {
    doReportPollCheck,
    reportRequestData,
    reportUUID,
    setDoReportPollCheck,
    setReportRequestData,
    setReportUUID,
    currentSelectedValues,
    setCurrentSelectedValues,
    isLoading,
  } = useCheckReportFile({
    onSuccessFn: onSuccessForIndividualReport,
    keepPollingStatusCodes: keepPollingStatusCodesForSingleExcelExport,
    successMessage: 'Excel is downloading',
    defaultErrorMessage: 'An error occurred while checking the Excel Report',
    maxAttemptsMessage:
      'Looks like the report is taking longer than expected. Please check the document\'s page later under the \'Reports\' folder.',
    infoMessage:
      'The report is taking longer than expected due to server timeouts. Please check the document\'s page later under the \'Reports\' folder.',
    pollingFn: ApiService.apiInvestorAppCheckExcelReportCreate,
  });

  const [activeCompany, setActiveCompany] = useState<FirmCompany | null>(null);
  const [pageTitle, setPageTitle] = useState();
  const [pageFilters, setPageFilters] = useState<FilterOption[]>([]);
  const [navItems, setNavItems] = useState();
  const [pageActions, setPageActions] = useState<MenuOptionsProps | null>(null);
  const [extraPageActions, setExtraPageActions] = useState<ActionProps[] | null>(null);
  const [showPageForm, setShowPageForm] = useState(false);
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState();
  const [showAlert, setShowAlert] = useState();
  const [alertMessage, setAlertMessage] = useState();
  const [editMode, setEditMode] = useState(false);
  const [extendedCompanyInfo, setExtendedCompanyInfo] = useState<ExtendedCompanyInfo | null>(null);
  const [companyExchangeRate, setCompanyExchangeRate] = useState<string>();
  const [financialExchangeRate, setFinancialExchangeRate] = useState<string>();
  const [pageType, setPageType] = useState(props?.pageType);
  const [commonMeasurementDate, setCommonMeasurementDate] = useState<MeasurementDate | null>(null);
  const [currentMeasurementDate, setCurrentMeasurementDate] = useState<MeasurementDate | null>(null);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [openBulkActionsDialog, setOpenBulkActionsDialog] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [fundsByMeasurementDate, setFundsByMeasurementDate] = useState([]);

  const [
    {
      isFirmStepperVisible,
      firmList,
      companyList,
      firmId,
      openCompanyDialog,
      fundInfo,
      companyInfo,
      otherPermissions,
      user,
    },
    dispatch,
  ] = useStore() as unknown as UseStoreValuesExtended;

  // Fund Dialog Form
  const displayFundDialogForm = useDialogFormsStore(state => state.displayFundDialogForm);
  const setDisplayFundDialogForm = useDialogFormsStore(state => state.setDisplayFundDialogForm);
  const setShouldUpdateFund = useDialogFormsStore(state => state.setShouldUpdateFund);

  // Fund Investment Dialog Form
  const displayFundInvestmentDialogForm = useDialogFormsStore(state => state.displayFundInvestmentDialogForm);
  const setDisplayFundInvestmentDialogForm = useDialogFormsStore(state => state.setDisplayFundInvestmentDialogForm);
  const setShouldUpdateFundInvestment = useDialogFormsStore(state => state.setShouldUpdateFundInvestment);

  const toggleExportDialog = useCallback(() => {
    setOpenExportDialog(prevState => !prevState);
  }, []);

  const toggleBulkActionsDialog = useCallback(() => {
    setOpenBulkActionsDialog(v => !v);
  }, []);

  const togglePdfDialog = useCallback(() => {
    setOpenPdfDialog(prevState => !prevState);
  }, []);

  const PAGES_WITHOUT_EXTRA_ACTIONS = [DOCUMENTS_SLUG, FIRM_REQUESTS_SLUG];

  // 0. try to determine this first to see if all the other firm calls should be made
  useEffect(() => {
    if (user?.id && isUndefined(otherPermissions)) {
      getLowerUserPermissions(user.id);
    }
  }, [user, otherPermissions]);

  const isSinglePermissionUser = useMemo(() => {
    if (isUndefined(otherPermissions)) {
      return undefined;
    }
    return Array.isArray(otherPermissions) && otherPermissions.length === 1 && user?.is_superuser === false;
  }, [otherPermissions, user]);

  const shouldMakeFirmCalls = useMemo(
    () => location.pathname.includes(String(firmId)) && !isUndefined(isSinglePermissionUser) && !isSinglePermissionUser,
    [firmId, isSinglePermissionUser]
  );

  const startSingleCompanyHook = useMemo(() => {
    if (isSinglePermissionUser === false) {
      return null;
    }
    if (isSinglePermissionUser) {
      // there shouldn't be any fund permission to return true
      return !otherPermissions?.some(({ feature_object }) => feature_object?.object_type === 'Fund');
    }
    return null;
  }, [isSinglePermissionUser]);
  // will run if argument is true
  useHandleSingleCompanyUser(startSingleCompanyHook!);
  // will run if argument is false
  useHandleSingleFundUser(startSingleCompanyHook!);

  // Fund Dialog
  const isFundPageActive = useMemo(() => !isEmpty(fundInfo), [fundInfo]);

  const onCloseFundDialog = useCallback(() => {
    // Close Fund Dialog Form
    setDisplayFundDialogForm(false);
    setShouldUpdateFund(false);

    setShowPageForm(false);
  }, []);

  // Fund Investment Dialog
  const onCloseFundInvestmentDialog = useCallback(() => {
    // Close Fund Investment Dialog Form
    setDisplayFundInvestmentDialogForm(false);
    setShouldUpdateFundInvestment(false);
  }, []);

  // 1. Identify active firm
  const activeFirm = useMemo(() => {
    if ((firmList as any) === ERROR_403) {
      navigate(forbiddenPath);
      return null;
    }
    if (isEmpty(firmList) || !firmSlugParam) {
      return null;
    }

    const currentActiveFirm = firmList?.find(f => f.slug === firmSlugParam);

    if (currentActiveFirm) {
      return currentActiveFirm;
    }
    /*
      The firmList contains only the firms where the user has access, so if the user tries to
      access using a url with firm slug that doesn't exist on firmList, the user will be redirected
      to forbidden path. For the combination of firmSlugParam and tableSlugParam below, a superuser
      should get a valid path.
    */
    if (firmSlugParam === '0' && tableSlugParam === 'comp-groups') {
      return null;
    }
    navigate(forbiddenPath);
    return null;
  }, [firmSlugParam, firmList]);

  // 1. Identify active company
  useEffect(() => {
    if (companyList && !isEmpty(companyList) && companySlugParam) {
      const company = companyList.find(item => item.company_slug === companySlugParam);
      if (company) {
        setActiveCompany(company);
      }
    }
  }, [companyList, companySlugParam]);

  useEffect(() => {
    if (!isEmpty(activeFirm)) {
      dispatch(firmsAction.setFirmId(activeFirm.id));
      dispatch(firmsAction.setFirmInfo(activeFirm));
    }
  }, [activeFirm]);

  const togglePageForm = useCallback(() => {
    setShowPageForm(!showPageForm);
  }, [showPageForm]);

  const toggleMainForm = useCallback(async () => {
    if (isFundPageActive) {
      const updatedFund = await fundsAction.getFundById(fundInfo?.id as number);
      dispatch(fundsAction.setFundInfo(updatedFund as Fund));

      // Open Fund Dialog Form
      setShouldUpdateFund(isFundPageActive);
      setDisplayFundDialogForm(isFundPageActive);
    }

    if (!isEmpty(activeCompany)) {
      setEditMode(true);
      dispatch(globalAction.showCompanyDialog(!openCompanyDialog));
      setShowPageForm(!showPageForm);
    }
  }, [activeCompany, fundInfo]);

  const getActiveCompanyExtendedInfo = useCallback(async () => {
    const firmService = new FirmService();
    const response = await firmService.getFirmCompanyExtendedInfo(
      activeFirm?.id as number,
      activeCompany?.company_id as number
    );
    setExtendedCompanyInfo(response);
  }, [activeCompany]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        (activeFirm?.id && activeCompany?.company_id) ||
        (activeFirm?.id && activeCompany?.company_id && !currentMeasurementDate)
      ) {
        await getActiveCompanyExtendedInfo();
      }
    };

    fetchData();
  }, [activeCompany, currentMeasurementDate]);

  const getCompanyExchangeRate = useCallback(async () => {
    if (!isEmpty(companyInfo)) {
      const financialsCurrency = getCurrency({
        companyInfo,
        page: 'financials',
      }) as string;
      const captableCurrency = getCurrency({
        companyInfo,
        page: 'captable',
      }) as string;
      const exchangeRate = await getExchangeRate(
        financialsCurrency,
        captableCurrency,
        getNumberValue(currentMeasurementDate?.id ?? companyInfo?.measurement_date_id).toString()
      );

      setCompanyExchangeRate(exchangeRate);
      const tempFinancialExchangeRate = await getExchangeRate(
        captableCurrency,
        financialsCurrency,
        getNumberValue(currentMeasurementDate?.id ?? companyInfo?.measurement_date_id).toString()
      );
      setFinancialExchangeRate(tempFinancialExchangeRate);
    }
  }, [companyInfo]);

  useEffect(() => {
    const fetchData = async () => {
      if (companyInfo) {
        await getCompanyExchangeRate();
      }
    };

    fetchData();
  }, [activeCompany, currentMeasurementDate]);

  // 2. Get company info base on companySlugParam
  useEffect(() => {
    if (!isEmpty(activeCompany) && !isEmpty(extendedCompanyInfo)) {
      /*
        We pass the active company information to the hook
        to avoid setting the information in the global store twice.
        Remove properties to avoid duplicated properties in companyInfo
      */
      const requiredProperties = {
        id: activeCompany.company_id,
        name: activeCompany.company_name,
        slug: activeCompany.company_slug,
        vintage_year: activeCompany.company_vintage_year,
        ...extendedCompanyInfo,
      };
      delete requiredProperties.company_slug;
      delete requiredProperties.company_name;
      delete requiredProperties.company_id;
      setCurrentSelectedValues({});
      dispatch(companiesAction.setCompanyInfo({ ...requiredProperties } as Partial<CompanyInfo>));
    }
  }, [extendedCompanyInfo]);

  // GET list of firms
  useEffect(() => {
    async function fetchFirmList() {
      const { firms, field_attributes: fieldAttributes } = await firmsAction.getFirmList();
      if (firms !== ERROR_403) {
        dispatch(firmsAction.setFirmList(firms));
        dispatch(firmsAction.setFirmListAuthorized(true));
      } else {
        dispatch(firmsAction.setFirmList(null));
        dispatch(firmsAction.setFirmListAuthorized(false));
      }
      dispatch(globalAction.setFieldAttrs(fieldAttributes));
    }
    fetchFirmList();
  }, [dispatch]);

  // GET firm information
  useEffect(() => {
    if (firmId && (shouldMakeFirmCalls || firmList?.length === 1)) {
      (async () => {
        const response = (await firmsAction.getFirmById(firmId)) as FirmResponseParams;
        if (response?.firmInfo) {
          dispatch(firmsAction.setFirmInfo(response.firmInfo));
        }
      })();
    }
  }, [firmId, shouldMakeFirmCalls]);

  // GET all the companies and funds in the current firm
  useEffect(() => {
    if (firmId && (shouldMakeFirmCalls || firmList?.length === 1)) {
      getFunds(firmId);
      getCompanies(firmId);
    }
  }, [firmId, shouldMakeFirmCalls]);

  // add event listener for hotkeys to save
  useEffect(() => {
    const handleKeyupForSave = (event: KeyboardEvent) => {
      const key = event.key || event.code;
      const periodKeyHitted = [PERIOD_CODE, PERIOD_CHAR].includes(key);
      // most likely to work is ctrl + .
      const initiatingKey = event.ctrlKey || event.metaKey || event.altKey;
      if (initiatingKey && periodKeyHitted && pageActions?.mainAction) {
        pageActions.mainAction.callback();
      }
    };

    if (!isEmpty(pageActions) && pageActions?.mainAction.label === 'Save') {
      document.addEventListener('keyup', handleKeyupForSave);
    }
    return () => document.removeEventListener('keyup', handleKeyupForSave);
  }, [pageActions]);

  const currentFirmRole = useMemo(() => {
    if (firmId && user?.firms_permissions) {
      return user.firms_permissions.find(({ id }) => Number(id) === Number(firmId))?.user_firm_role;
    }
  }, [firmId, user]);

  const viewOnlyUser = useMemo(() => {
    if (currentFirmRole) {
      return VIEW_ONLY_NO_ACTIONS_ROLES_VALUES.includes(currentFirmRole);
    }
    return false;
  }, [currentFirmRole]);

  const displayMemoizedFilters = useMemo(() => {
    if (pageFilters) {
      const validPageFilters = pageFilters.filter(filterItem => filterItem.selectedValue);
      return validPageFilters.map(filter => (
        <StyledGridFilter key={filter.id} item>
          <Filter
            {...filter}
            id={filter.id}
            label={filter.title}
            handleFilter={filter.handler}
            hideActions={viewOnlyUser}
          />
        </StyledGridFilter>
      ));
    }
    return null;
  }, [pageFilters, viewOnlyUser]);

  const shouldDisplayGrayBg = useMemo(
    () => location.pathname.includes(DOCUMENTS_SLUG) || location.pathname.includes(FIRM_REQUESTS_SLUG),
    [location?.pathname]
  );

  const updatePageActions = useCallback(
    (newActions: MenuOptionsProps) => {
      if (viewOnlyUser) {
        setPageActions(null);
      } else {
        setPageActions(newActions);
      }
    },
    [viewOnlyUser, setPageActions]
  );

  const updateExtraPageActions = useCallback(
    (extraActions: ActionProps[]) => {
      if ([FULL_ACCESS_VIEWER_VALUE, EXPORT_VIEWER_VALUE, AUDITOR_VALUE].includes(currentFirmRole!)) {
        setExtraPageActions(extraActions.filter(action => action.label === EXCEL_EXPORT));
      } else if (viewOnlyUser || PAGES_WITHOUT_EXTRA_ACTIONS.some(page => location.pathname.includes(page))) {
        setExtraPageActions(null);
      } else {
        setExtraPageActions(extraActions);
      }
    },
    [currentFirmRole, viewOnlyUser, setExtraPageActions, location.pathname]
  );

  const isExportMode = useMemo(
    () => searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_MODE) === 'true',
    [searchParams]
  );

  const exportTab = useMemo(
    () => searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TAB),
    [searchParams]
  );

  const excelExportContextValue = useMemo(
    () =>
      ({
        doReportPollCheck,
        reportRequestData,
        reportUUID,
        setDoReportPollCheck,
        setReportRequestData,
        setReportUUID,
        fundsByMeasurementDate,
        setFundsByMeasurementDate,
        currentSelectedValues,
        setCurrentSelectedValues,
        isLoading,
      }) as unknown as ExcelExportContextType,
    [
      doReportPollCheck,
      reportRequestData,
      reportUUID,
      setDoReportPollCheck,
      setReportRequestData,
      setReportUUID,
      fundsByMeasurementDate,
      setFundsByMeasurementDate,
      currentSelectedValues,
      setCurrentSelectedValues,
      isLoading,
    ]
  );

  const layoutContextValue = useMemo(
    () =>
      ({
        pageTitle,
        setPageTitle,
        pageFilters,
        setPageFilters,
        navItems,
        setNavItems,
        pageActions,
        setPageActions,
        showPageForm,
        setShowPageForm,
        togglePageForm,
        pageBreadcrumbs,
        setPageBreadcrumbs,
        showAlert,
        setShowAlert,
        alertMessage,
        setAlertMessage,
        toggleMainForm,
        companyExchangeRate,
        pageType,
        setPageType,
        extraPageActions,
        commonMeasurementDate,
        setCommonMeasurementDate,
        openExportDialog,
        toggleExportDialog,
        openBulkActionsDialog,
        toggleBulkActionsDialog,
        openPdfDialog,
        togglePdfDialog,
        currentMeasurementDate,
        setCurrentMeasurementDate,
        financialExchangeRate,
        updatePageActions,
        updateExtraPageActions,
        viewOnlyUser,
        currentFirmRole,
        isExportMode,
        exportTab,
      }) as unknown as LayoutContextType,
    [
      pageTitle,
      setPageTitle,
      pageFilters,
      setPageFilters,
      navItems,
      setNavItems,
      pageActions,
      setPageActions,
      showPageForm,
      setShowPageForm,
      togglePageForm,
      pageBreadcrumbs,
      setPageBreadcrumbs,
      showAlert,
      setShowAlert,
      alertMessage,
      setAlertMessage,
      toggleMainForm,
      companyExchangeRate,
      pageType,
      setPageType,
      extraPageActions,
      commonMeasurementDate,
      setCommonMeasurementDate,
      openExportDialog,
      toggleExportDialog,
      openBulkActionsDialog,
      toggleBulkActionsDialog,
      currentMeasurementDate,
      setCurrentMeasurementDate,
      financialExchangeRate,
      updatePageActions,
      updateExtraPageActions,
      viewOnlyUser,
      currentFirmRole,
      isExportMode,
      exportTab,
      openPdfDialog,
      togglePdfDialog,
    ]
  );

  const scalarCookieExists = useMemo(() => {
    if (user?.id) {
      return checkCookie(`${SCALAR_OTP_COOKIE}_${user.id}`);
    }

    return false;
  }, [user]);

  if (user?.has_2fa && !localStorage.getItem('isOtpVerified') && isFalseStrict(scalarCookieExists)) {
    navigate(SIGN_IN_URL);
  }

  return (
    <Root shouldDisplayGrayBg={shouldDisplayGrayBg}>
      <LayoutContext.Provider value={layoutContextValue}>
        <ExcelExportContext.Provider value={excelExportContextValue}>
          <Sidebar variant="permanent" onClose={() => {}} />
          <Header>{displayMemoizedFilters}</Header>
          <Content shouldDisplayGrayBg={shouldDisplayGrayBg}>
            {showAlert && <Alert isAlertVisible={showAlert}>{alertMessage}</Alert>}
            {children}
          </Content>

          {/* Fund Form Dialog */}
          {displayFundDialogForm && <FundDialogForm handleOnClose={onCloseFundDialog} open />}

          {/* Firm Stepper Dialog */}
          {isFirmStepperVisible && (
            <FirmStepper
              isVisible={isFirmStepperVisible}
              onClose={() => dispatch(globalAction.showFirmStepper(false))}
            />
          )}

          {/* Company Form Dialog */}
          {openCompanyDialog && (
            <NewCompanyDialog
              open={openCompanyDialog}
              onClose={() => {
                dispatch(globalAction.showCompanyDialog(false));
                setShowPageForm(false);
                setEditMode(false);
              }}
              editMode={editMode}
            />
          )}

          {/* Fund Investment Form Dialog */}
          {displayFundInvestmentDialogForm && (
            <FundInvestmentFormDialog handleOnClose={onCloseFundInvestmentDialog} open />
          )}
        </ExcelExportContext.Provider>
      </LayoutContext.Provider>

      <Footer shouldDisplayGrayBg={shouldDisplayGrayBg} />
    </Root>
  );
};

export default Main;
